import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SubPageLayout from '../helpers/SubPageLayout';
import { ActionsColumn, DetailsCard } from '../helpers/Components';
import {
  Button,
  Empty,
  Spin,
  Switch,
  Modal,
  Select,
  DatePicker,
  Table,
  Tooltip,
  Tag,
  Divider,
  Input,
} from 'antd';
import {
  PlusOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  ToolOutlined,
  ApartmentOutlined,
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  EditOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import MyTable from '../helpers/MyTable';
import { useDispatch, useSelector } from 'react-redux';
import QR from 'qrcode';
import QrCode from '../helpers/QRCode';
import printJS from 'print-js';
import { toast } from 'react-toastify';
import eng from '../../../utils/eng';
import moment from 'moment';
import { isFeatureEnabled, getIsSuperAdmin, getUser, getIsAdminApi } from '../../../features/auth.store';
import {
  enableDisabledChamber,
  setMoveFromCompany,
  setMoveFromSite,
  setMoveToCompany,
  setMoveToSite,
  setMoveSelectedChamber,
  claerMoveInfo,
  moveChamber,
} from '../../../features/chamber.store';
import {
  fetchMinimalCompanyList,
  getMinimalCompanyList,
  getMinimalCompanyListLoading,
} from '../../../features/company.store';
import { DEFAULT_PAGE_SIZE, DATE_FORMAT } from '../../../utils/constant';
import { ROLE } from '../../../utils/features';
import get from 'lodash.get';
import { MoveChamberModal } from '../helpers/Modals';
import { addMonths, format } from 'date-fns';
import { yesNo } from '../../../utils/utils';
import { GET } from '../../../service/base-api-service';
import { downloadServiceExpiryReport } from '../../../utils/report-download';

// ---------- Helpers ----------

const cleanParams = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined && v !== null && v !== ''));

const printArray = (chamber, path) =>
  get(chamber, path, [])
    .map(({ displayName }) => displayName)
    .join(', ');

const v = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'string' && value.trim() === '') return 'N/A';
  return value;
};

const niceBool = (value) => (value === null || value === undefined ? 'N/A' : yesNo(value));

const calculateNextServiceDue = (lastServicedDate, serviceFrequencyMonths) => {
  if (!lastServicedDate || serviceFrequencyMonths === null || serviceFrequencyMonths === undefined) {
    return 'N/A';
  }

  const parsedFrequency = Number(serviceFrequencyMonths);
  if (Number.isNaN(parsedFrequency) || parsedFrequency <= 0) {
    return 'N/A';
  }

  const lastServiceDate = new Date(lastServicedDate);
  if (Number.isNaN(lastServiceDate.getTime())) {
    return 'N/A';
  }

  return format(addMonths(lastServiceDate, parsedFrequency), DATE_FORMAT);
};

const normalizeRoles = (user) => {
  const scope = get(user, 'scope');
  const role = get(user, 'role');
  const roles = get(user, 'roles', []);
  const authorities = get(user, 'authorities', []);
  const authorityNames = Array.isArray(authorities)
    ? authorities.map((a) => (typeof a === 'string' ? a : a?.authority)).filter(Boolean)
    : [];

  const splitScope =
    typeof scope === 'string' ? scope.split(',').map((s) => s.trim()).filter(Boolean) : [];

  const all = [
    ...splitScope,
    ...(role ? [role] : []),
    ...(Array.isArray(roles) ? roles : []),
    ...authorityNames,
  ].filter(Boolean);

  return Array.from(new Set(all));
};

const hasRole = (user, roleConst) => normalizeRoles(user).includes(roleConst);

const getTenantIdentifierFromUser = (user) =>
  get(user, 'tenant.identifier') ||
  get(user, 'tenant.id') ||
  get(user, 'tenant_identifier') ||
  get(user, 'tenantId') ||
  '';

const getTenantNameFromUser = (user) =>
  get(user, 'tenant.name') ||
  get(user, 'tenant_name') ||
  '';

const getUserSiteIdFromUser = (user) =>
  get(user, 'site.id') || get(user, 'site_id') || get(user, 'siteId') || '';

const parseUserSiteIds = (raw) => {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw
      .flatMap((vv) => String(vv).split(','))
      .map((vv) => String(vv).trim())
      .filter(Boolean)
      .map((vv) => (Number.isNaN(Number(vv)) ? vv : Number(vv)));
  }

  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((vv) => vv.trim())
      .filter(Boolean)
      .map((vv) => (Number.isNaN(Number(vv)) ? vv : Number(vv)));
  }

  return [raw];
};

const getUserSiteIdsFromUser = (user) => {
  const userSites = get(user, 'user_sites');
  const parsed = parseUserSiteIds(userSites);

  if (parsed.length > 0) return Array.from(new Set(parsed));

  const singleSiteId = getUserSiteIdFromUser(user);
  if (singleSiteId !== '' && singleSiteId !== null && singleSiteId !== undefined) {
    return [Number.isNaN(Number(singleSiteId)) ? singleSiteId : Number(singleSiteId)];
  }

  return [];
};

const normalizeId = (value) => String(value);

const getTenantIdentifierFromSite = (site) =>
  get(site, 'tenant.identifier') ||
  get(site, 'tenant.id') ||
  get(site, 'tenantIdentifier') ||
  get(site, 'tenant_identifier') ||
  get(site, 'company.identifier') ||
  get(site, 'company.id') ||
  '';

const getTenantNameFromSite = (site) =>
  get(site, 'tenant.name') ||
  get(site, 'tenant_name') ||
  get(site, 'company.name') ||
  '';

const getSiteIdFromChamber = (chamber) =>
  get(chamber, 'site.id') ||
  get(chamber, 'siteId') ||
  get(chamber, 'site_id') ||
  get(chamber, 'siteIdentifier') ||
  null;

const getSiteNameFromChamber = (chamber) =>
  get(chamber, 'site.name') ||
  get(chamber, 'siteName') ||
  get(chamber, 'site_name') ||
  '';

const getTenantIdentifierFromChamber = (chamber) =>
  get(chamber, 'tenant.identifier') ||
  get(chamber, 'tenant.id') ||
  get(chamber, 'tenantIdentifier') ||
  get(chamber, 'tenant_identifier') ||
  get(chamber, 'company.identifier') ||
  get(chamber, 'company.id') ||
  '';

const getTenantNameFromChamber = (chamber) =>
  get(chamber, 'tenant.name') ||
  get(chamber, 'tenantName') ||
  get(chamber, 'tenant_name') ||
  get(chamber, 'company.name') ||
  '';

const getTenantIdentifierFromCompany = (company) =>
  get(company, 'identifier') ||
  get(company, 'tenant.identifier') ||
  get(company, 'tenantId') ||
  get(company, 'id') ||
  '';

const getTenantNameFromCompany = (company) =>
  get(company, 'name') ||
  get(company, 'tenant.name') ||
  '';

const buildTenantOptionsFromCompanies = (companies) =>
  (Array.isArray(companies) ? companies : [])
    .map((company) => {
      const value = getTenantIdentifierFromCompany(company);
      const label = getTenantNameFromCompany(company) || value;
      return value ? { value, label } : null;
    })
    .filter(Boolean)
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));

const buildTenantOptionsFallback = (sites, chambers = []) => {
  const map = new Map();

  sites.forEach((site) => {
    const tenantId = getTenantIdentifierFromSite(site);
    const tenantLabel = getTenantNameFromSite(site);
    if (tenantId) {
      map.set(tenantId, tenantLabel || map.get(tenantId) || tenantId);
    }
  });

  chambers.forEach((chamber) => {
    const tenantId = getTenantIdentifierFromChamber(chamber);
    const tenantLabel = getTenantNameFromChamber(chamber);
    if (tenantId && tenantLabel) {
      map.set(tenantId, tenantLabel);
    }
  });

  return Array.from(map.entries())
    .map(([value, label]) => ({ value, label: label || value }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
};

const filterSitesByTenant = (sites, tenantId) => {
  if (!tenantId) return sites;
  return sites.filter((site) => normalizeId(getTenantIdentifierFromSite(site)) === normalizeId(tenantId));
};

const dedupeById = (rows) => {
  const map = new Map();
  rows.forEach((row) => {
    const id = get(row, 'id');
    if (id !== undefined && id !== null) map.set(id, row);
  });
  return Array.from(map.values());
};

const normalizeChamberRow = (chamber, sites = []) => {
  const siteId = getSiteIdFromChamber(chamber);
  const matchedSite = sites.find((site) => normalizeId(get(site, 'id')) === normalizeId(siteId));

  const tenantIdentifier =
    getTenantIdentifierFromChamber(chamber) ||
    getTenantIdentifierFromSite(matchedSite) ||
    '';

  const tenantName =
    getTenantNameFromChamber(chamber) ||
    getTenantNameFromSite(matchedSite) ||
    '';

  const siteName =
    getSiteNameFromChamber(chamber) ||
    get(matchedSite, 'name') ||
    '';

  return {
    ...chamber,
    siteId,
    tenantIdentifier,
    tenantName,
    siteName,
    site: {
      ...(get(chamber, 'site') || {}),
      id: siteId,
      name: get(chamber, 'site.name') || siteName,
    },
    tenant: {
      ...(get(chamber, 'tenant') || {}),
      identifier: get(chamber, 'tenant.identifier') || tenantIdentifier,
      name: get(chamber, 'tenant.name') || tenantName,
    },
  };
};

const normalizeChambers = (chambers, sites = []) =>
  dedupeById((Array.isArray(chambers) ? chambers : []).map((row) => normalizeChamberRow(row, sites)));

const buildCacheKeyBase = ({ shouldUseAdminApi, hasGlobalChamberAccess, tenantIdentifier, assignedSiteIds }) => {
  const tenantPart = hasGlobalChamberAccess ? 'GLOBAL' : tenantIdentifier || 'NO_TENANT';
  const sitePart = assignedSiteIds.map((id) => normalizeId(id)).sort().join('_') || 'NO_SITES';
  return `CHAMBERS_V3_${shouldUseAdminApi ? 'ADMIN' : 'USER'}_${tenantPart}_${sitePart}`;
};

const clearAllSessionCache = () => {
  try {
    sessionStorage.clear();
  } catch (e) {
    console.warn('Failed to clear sessionStorage', e);
  }
};

const readJsonCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.warn('Cache read failed:', key, e);
    return null;
  }
};

const writeJsonCache = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn('Cache write failed:', key, e);
  }
};

const Pill = ({ label, icon, value, tone = 'default' }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 12px',
      borderRadius: 12,
      border: '1px solid #f0f0f0',
      background: '#fff',
      boxShadow: '0 1px 0 rgba(0,0,0,0.02)',
      minHeight: 54,
    }}
  >
    <div
      style={{
        width: 34,
        height: 34,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #f0f0f0',
        background:
          tone === 'success'
            ? 'rgba(82,196,26,0.10)'
            : tone === 'warning'
            ? 'rgba(250,173,20,0.12)'
            : 'rgba(24,144,255,0.10)',
      }}
    >
      {icon}
    </div>
    <div style={{ lineHeight: 1.1 }}>
      <div style={{ fontSize: 12, opacity: 0.7, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>{value}</div>
    </div>
  </div>
);

const DetailRow = ({ label, value }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '260px 1fr',
      gap: 12,
      padding: '10px 0',
      borderBottom: '1px solid #f5f5f5',
    }}
  >
    <div style={{ fontWeight: 700, opacity: 0.85 }}>{label}</div>
    <div style={{ opacity: 0.9 }}>{value}</div>
  </div>
);

const SectionCard = ({ title, children, right }) => (
  <div
    style={{
      background: '#fff',
      border: '1px solid #f0f0f0',
      borderRadius: 14,
      padding: 14,
      boxShadow: '0 1px 0 rgba(0,0,0,0.02)',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 900 }}>{title}</div>
      {right}
    </div>
    <div style={{ marginTop: 10 }}>{children}</div>
  </div>
);

const FilterField = ({ label, children }) => (
  <div style={{ minWidth: 0 }}>
    <div
      style={{
        fontSize: 12,
        fontWeight: 800,
        color: '#6b7280',
        marginBottom: 8,
        letterSpacing: '0.2px',
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

const ChamberDetailsContent = ({ chamber }) => {
  if (!chamber) return null;

  const chamberTypeName = get(chamber, 'chamberTypeEnum.name');
  const heroImg = chamberTypeName
    ? `https://res.cloudinary.com/day4l7awk/image/upload/h_160,w_160,c_fill/chamber-types/${chamberTypeName}.png`
    : `https://res.cloudinary.com/day4l7awk/image/upload/h_160,w_160,c_fill/v1653323984/chamber-types/DEFAULT.jpg`;

  const lastServiced = get(chamber, 'lastServicedDate')
    ? format(new Date(get(chamber, 'lastServicedDate')), DATE_FORMAT)
    : 'N/A';
  const nextServiceDue = calculateNextServiceDue(get(chamber, 'lastServicedDate'), get(chamber, 'serviceFrequency'));

  const enabled = get(chamber, 'isEnabled', null);

  return (
    <div style={{ padding: 10 }}>
      <div
        style={{
          borderRadius: 16,
          padding: 16,
          border: '1px solid #f0f0f0',
          background:
            'linear-gradient(135deg, rgba(0, 128, 0, 0.06) 0%, rgba(24, 144, 255, 0.06) 60%, rgba(250, 173, 20, 0.05) 100%)',
          display: 'flex',
          gap: 14,
          alignItems: 'center',
        }}
      >
        <img
          src={heroImg}
          alt="chamber"
          style={{
            width: 74,
            height: 74,
            borderRadius: 14,
            objectFit: 'cover',
            border: '1px solid rgba(0,0,0,0.06)',
            background: '#fff',
          }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div
              style={{
                fontSize: 18,
                fontWeight: 950,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {v(get(chamber, 'name'))}
            </div>

            <Tag
              color={enabled === null ? 'default' : enabled ? 'green' : 'red'}
              style={{ margin: 0, borderRadius: 999, fontWeight: 700 }}
            >
              {enabled === null ? 'UNKNOWN' : enabled ? 'ACTIVE' : 'INACTIVE'}
            </Tag>
          </div>

          <div style={{ marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap', opacity: 0.85 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <ApartmentOutlined /> {v(get(chamber, 'tenant.name'))}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <HomeOutlined /> {v(get(chamber, 'site.name'))}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <EnvironmentOutlined /> {v(get(chamber, 'buildLocationEnum.displayName'))}
            </span>
          </div>

          <div style={{ marginTop: 10, fontWeight: 800, opacity: 0.9 }}>
            Type: <span style={{ fontWeight: 900 }}>{v(get(chamber, 'chamberTypeEnum.displayName'))}</span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Pill label="Last Serviced" icon={<CalendarOutlined />} value={lastServiced} tone="success" />
        <Pill label="Next Service Due" icon={<ToolOutlined />} value={nextServiceDue} tone="warning" />
      </div>

      <Divider style={{ margin: '14px 0' }} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <SectionCard
          title="Core configuration"
          right={
            <Tag style={{ margin: 0, borderRadius: 999 }} icon={<ToolOutlined />}>
              {v(get(chamber, 'scrubberTypeEnum.displayName'))}
            </Tag>
          }
        >
          <DetailRow label="RSP Kit Number" value={v(get(chamber, 'rspKitNo'))} />
          <DetailRow label="Capacity" value={v(get(chamber, 'capacity'))} />
          <DetailRow label="Duration" value={v(get(chamber, 'duration'))} />
          <DetailRow label="Chamber Type" value={v(get(chamber, 'chamberTypeEnum.displayName'))} />
          <DetailRow label="Scrubber Type" value={v(get(chamber, 'scrubberTypeEnum.displayName'))} />
          <DetailRow label="Chemical Type" value={v(get(chamber, 'chemicalTypeEnum.displayName'))} />
          <DetailRow label="Compressed air filtration" value={v(get(chamber, 'airFilterTypeEnum.displayName'))} />
          <DetailRow label="Interior Lighting" value={v(get(chamber, 'interiorLightingTypeEnum.displayName'))} />
          <DetailRow label="Options" value={v(printArray(chamber, 'optionsTypeEnums'))} />
        </SectionCard>

        <SectionCard title="Gas monitoring">
          <DetailRow label="Digital gas monitoring" value={v(get(chamber, 'gasMonitorTypeEnum.displayName'))} />
          <DetailRow label="Gas sensors" value={v(printArray(chamber, 'gasSensorTypeEnums'))} />
          <DetailRow
            label="External Node Gas sensors"
            value={v(printArray(chamber, 'externalNodeGasSensorTypeEnums'))}
          />
        </SectionCard>

        <SectionCard title="Consumables">
          <DetailRow label="CO tube quantity" value={v(get(chamber, 'coTubeQuantity'))} />
          <DetailRow label="CO2 tube quantity" value={v(get(chamber, 'co2TubeQuantity'))} />
          <DetailRow label="Battery quantity" value={v(get(chamber, 'batteryQuantity'))} />
          <DetailRow label="Additional Language" value={v(get(chamber, 'additionalLanguages'))} />
          <DetailRow label="CO chemical quantity" value={v(get(chamber, 'coChemicalQuantity'))} />
          <DetailRow label="CO2 chemical quantity" value={v(get(chamber, 'co2ChemicalQuantity'))} />
          <DetailRow label="Moisture removal quantity" value={v(get(chamber, 'moistureRemovalQuantity'))} />
        </SectionCard>

        <SectionCard title="Safety & emergency">
          <DetailRow label="Medical oxygen (main) qty" value={v(get(chamber, 'mainChamberOxygenLevel'))} />
          <DetailRow label="Medical oxygen (PPMS) qty" value={v(get(chamber, 'ppmsOxygenLevel'))} />
          <DetailRow label="Medical oxygen (Airlock) qty" value={v(get(chamber, 'airlockOxygenLevel'))} />
          <DetailRow label="Oxygen candle" value={niceBool(get(chamber, 'oxygenCandle'))} />
          <DetailRow label="Manual gas tubes" value={niceBool(get(chamber, 'manualGasTubes'))} />
          <DetailRow label="Fire extinguisher - external" value={niceBool(get(chamber, 'externalFireExtinguisher'))} />
          <DetailRow label="Fire extinguisher - internal" value={niceBool(get(chamber, 'internalFireExtinguisher'))} />
          <DetailRow label="Fire blanket - internal" value={niceBool(get(chamber, 'fireBlanket'))} />
          <DetailRow label="Transformer" value={niceBool(get(chamber, 'transformer'))} />
          <DetailRow label="Communications box" value={niceBool(get(chamber, 'communicationsBox'))} />
          <DetailRow label="Toilet" value={niceBool(get(chamber, 'toilet'))} />
          <DetailRow label="Long-life food" value={niceBool(get(chamber, 'longLifeFood'))} />
        </SectionCard>
      </div>

      {/* <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            border: '1px dashed #d9d9d9',
            borderRadius: 14,
            padding: 12,
            background: '#fafafa',
            width: 340,
            textAlign: 'center',
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 8 }}>QR Code</div>
          <QrCode id={get(chamber, 'qrCode')} />
        </div>
      </div> */}
    </div>
  );
};

const buildColumns = (
  dispatch,
  canMoveChambers,
  canEditChambers,
  onQrCodeClick,
  onMoveClick,
  onScheduleClick,
  onDetailsClick,
  togglingChamberIds,
  onToggleChamber,
) => {
  const actionIconButtonStyle = {
    width: 34,
    height: 34,
    minWidth: 34,
    borderRadius: 10,
    border: '1px solid #d9efe3',
    background: '#f8fffb',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    boxShadow: '0 4px 10px rgba(15, 106, 79, 0.06)',
  };

  const actionIconImageStyle = {
    width: 16,
    height: 16,
    objectFit: 'contain',
  };

  const cols = [
    {
      title: 'Chamber Name',
      dataIndex: 'name',
      render: (name, record) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => onDetailsClick(record)}
          onKeyDown={(e) => e.key === 'Enter' && onDetailsClick(record)}
          style={{ cursor: 'pointer' }}
        >
          <DetailsCard
            name={name}
            id={record.id}
            className="d-flex f-row ai-center"
            as="div"
            src={`https://res.cloudinary.com/day4l7awk/image/upload/v1653323984/chamber-types/${get(
              record,
              'chamberTypeEnum.name',
            )}.jpg`}
          />
        </div>
      ),
    },
    { title: 'Company', dataIndex: ['tenant', 'name'] },
    { title: 'Site', dataIndex: ['site', 'name'] },
    {
      title: 'Active',
      dataIndex: 'isEnabled',
      render: (data, record) => (
        <Switch
          checked={data}
          disabled={togglingChamberIds.has(record.id)}
          onChange={() => onToggleChamber(record.id, data)}
        />
      ),
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
          {canMoveChambers && (
            <Tooltip title="Transfer">
              <Button
                size="small"
                style={actionIconButtonStyle}
                icon={<img src="/icons/copy-icon.svg" alt="Transfer" style={actionIconImageStyle} />}
                onClick={() => onMoveClick(record)}
              />
            </Tooltip>
          )}

          {canEditChambers && (
            <Tooltip title="Edit">
              <Link to={`/chambers/${record.id}/edit`}>
                <Button size="small" style={actionIconButtonStyle} icon={<EditOutlined style={{ color: '#0f6a4f' }} />} />
              </Link>
            </Tooltip>
          )}

          <Tooltip title="Reports">
            <Link
              to={{
                pathname: `/chambers/${record.id}/reports`,
                state: { chamberName: record.name },
              }}
            >
              <Button
                size="small"
                style={actionIconButtonStyle}
                icon={<img src="/icons/reports.svg" alt="Reports" style={actionIconImageStyle} />}
              />
            </Link>
          </Tooltip>
        </div>
      ),
      width: 140,
    },
  ];

  cols.splice(1, 0, {
    title: 'Print QR',
    dataIndex: 'qrCode',
    render: (data) => <ActionsColumn showQR onQrCodeClick={() => onQrCodeClick(data)} />,
  });

  return cols;
};

const Chambers = () => {
  const dispatch = useDispatch();

  const user = useSelector(getUser);
  const isSuperAdmin = useSelector(getIsSuperAdmin);
  const isAdminApi = useSelector(getIsAdminApi);
  const minimalCompanyList = useSelector(getMinimalCompanyList);
  const minimalCompanyListLoading = useSelector(getMinimalCompanyListLoading);

  const getFeatureEnable = useSelector(isFeatureEnabled);
  const featureAddChambers = getFeatureEnable('add_chambers');

  const resolvedRoles = useMemo(() => normalizeRoles(user), [user]);

  const isMinearcRole = useMemo(() => hasRole(user, ROLE.MINEARC), [user]);
  const isSiteRole = useMemo(() => hasRole(user, ROLE.SU), [user]);
  const isTenantRole = useMemo(() => hasRole(user, ROLE.TA), [user]);
  const isSuperAdminRole = useMemo(() => hasRole(user, ROLE.SA), [user]);
  const CanViewAllChambers = useMemo(() => hasRole(user, ROLE.VIEW_ALL_CHAMBERS), [user]);

  const hasGlobalChamberAccess = useMemo(
    () => isSuperAdmin || isSuperAdminRole || isMinearcRole,
    [isSuperAdmin, isSuperAdminRole, isMinearcRole],
  );

  const canEditChambers = useMemo(
    () => isSuperAdmin || isSuperAdminRole || isMinearcRole,
    [isSuperAdmin, isSuperAdminRole, isMinearcRole],
  );

  const shouldUseAdminApi = useMemo(
    () => isAdminApi || hasGlobalChamberAccess,
    [isAdminApi, hasGlobalChamberAccess],
  );

  const tenantIdentifier = useMemo(() => getTenantIdentifierFromUser(user), [user]);
  const tenantName = useMemo(() => getTenantNameFromUser(user), [user]);
  const assignedSiteIds = useMemo(() => getUserSiteIdsFromUser(user), [user]);
  const assignedSiteIdsSet = useMemo(
    () => new Set(assignedSiteIds.map((id) => normalizeId(id))),
    [assignedSiteIds],
  );

  const [currentPage, setPage] = useState(1);

  const [QRModalState, setQRModalState] = useState(false);
  const [QRModalStateLoading, setQRModalStateLoading] = useState(false);

  const [showMove, setShowMove] = useState(false);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedChamberForSchedule, setSelectedChamberForSchedule] = useState(null);
  const [serviceSelections, setServiceSelections] = useState({
    Commission: 'No',
    Audit: 'No',
    Service: 'No',
  });
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedChamberForDetails, setSelectedChamberForDetails] = useState(null);

  const [bootLoading, setBootLoading] = useState(true);
  const [allChambers, setAllChambers] = useState([]);
  const [allowedSites, setAllowedSites] = useState([]);
  const [allAccessibleSites, setAllAccessibleSites] = useState([]);
  const [seeAllMode, setSeeAllMode] = useState(false);

  const [searchName, setSearchName] = useState('');
  const [filterCompany, setFilterCompany] = useState(undefined);
  const [filterSite, setFilterSite] = useState(undefined);
  const [togglingChamberIds, setTogglingChamberIds] = useState(new Set());
  const [expiryReportLoading, setExpiryReportLoading] = useState(false);

  const didBootRef = useRef(false);
  const didClearRefreshCacheRef = useRef(false);

  const companyLocked = isTenantRole || isSiteRole;
  const siteLocked = false;

  const cacheBase = useMemo(
    () =>
      buildCacheKeyBase({
        shouldUseAdminApi,
        hasGlobalChamberAccess,
        tenantIdentifier,
        assignedSiteIds,
      }),
    [shouldUseAdminApi, hasGlobalChamberAccess, tenantIdentifier, assignedSiteIds],
  );

  const basePrefix = useMemo(() => (shouldUseAdminApi ? 'v1/admin' : 'v1'), [shouldUseAdminApi]);

  const companyOptions = useMemo(() => {
    if ((isTenantRole || isSiteRole) && tenantIdentifier) {
      return [
        {
          value: tenantIdentifier,
          label: tenantName || tenantIdentifier,
        },
      ];
    }

    const fromCompanyStore = buildTenantOptionsFromCompanies(minimalCompanyList);
    if (fromCompanyStore.length > 0) return fromCompanyStore;

    return buildTenantOptionsFallback(allAccessibleSites, allChambers);
  }, [
    allAccessibleSites,
    allChambers,
    isTenantRole,
    isSiteRole,
    minimalCompanyList,
    tenantIdentifier,
    tenantName,
  ]);

  const scopeSitesForUser = useCallback(
    (sites) => {
      let scopedSites = sites;

      if (isSiteRole) {
        scopedSites = sites.filter((site) => assignedSiteIdsSet.has(normalizeId(site.id)));
      } else if (isTenantRole && !hasGlobalChamberAccess) {
        const sitesWithTenantInfo = sites.filter((site) => !!getTenantIdentifierFromSite(site));

        if (sitesWithTenantInfo.length > 0 && tenantIdentifier) {
          scopedSites = sites.filter(
            (site) => normalizeId(getTenantIdentifierFromSite(site)) === normalizeId(tenantIdentifier),
          );
        }
      }

      return scopedSites;
    },
    [assignedSiteIdsSet, hasGlobalChamberAccess, isSiteRole, isTenantRole, tenantIdentifier],
  );

  const fetchSitesPage = useCallback(
    async (page, forcedTenantId) => {
      const url = shouldUseAdminApi ? 'v1/admin/site' : 'v1/site/all';

      const params = cleanParams({
        page,
        sortColumn: 'name',
        isAscending: true,
        size: 500,
        searchKey: '',
        ...(forcedTenantId ? { tenantId: forcedTenantId } : {}),
        ...(!forcedTenantId && tenantIdentifier && !hasGlobalChamberAccess ? { tenantId: tenantIdentifier } : {}),
      });

      const payload = await GET(url, { params });

      const results =
        payload?.results
          ? payload.results
          : Array.isArray(payload)
          ? payload
          : payload?.id
          ? [payload]
          : [];

      const totalPages = payload?.totalPages !== undefined ? payload.totalPages : 1;

      return { results, totalPages };
    },
    [shouldUseAdminApi, tenantIdentifier, hasGlobalChamberAccess],
  );

  const fetchAllSites = useCallback(
    async (forcedTenantId) => {
      const fetchedSites = [];
      let page = 0;
      let totalPages = 1;

      while (page < totalPages) {
        const res = await fetchSitesPage(page, forcedTenantId);
        fetchedSites.push(...res.results);
        totalPages = res.totalPages || 1;
        page += 1;
      }

      return fetchedSites;
    },
    [fetchSitesPage],
  );

  const fetchAllChambersForSites = useCallback(
    async (siteIds) => {
      if (!siteIds || siteIds.length === 0) return [];

      if (shouldUseAdminApi) {
        const payload = await GET(`${basePrefix}/site/chambers`, {
          params: {
            siteIds,
            page: 0,
            size: 10000,
            sortColumn: 'id',
            isAscending: false,
          },
          paramsSerializer: (params) => {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                value.forEach((vv) => usp.append(key, vv));
              } else if (value !== undefined && value !== null) {
                usp.append(key, value);
              }
            });
            return usp.toString();
          },
        });

        return payload?.results || [];
      }

      const requests = siteIds.map(async (siteId) => {
        try {
          const payload = await GET('v1/site/chambers', {
            params: {
              siteId,
              page: 0,
              size: 10000,
              sortColumn: 'id',
              isAscending: false,
            },
          });

          if (Array.isArray(payload)) return payload;
          if (Array.isArray(payload?.results)) return payload.results;
          if (payload?.id) return [payload];
          return [];
        } catch (firstErr) {
          try {
            const payload = await GET('v1/site/chambers', {
              params: {
                siteIds: [siteId],
                page: 0,
                size: 10000,
                sortColumn: 'id',
                isAscending: false,
              },
              paramsSerializer: (params) => {
                const usp = new URLSearchParams();
                Object.entries(params).forEach(([key, value]) => {
                  if (Array.isArray(value)) {
                    value.forEach((vv) => usp.append(key, vv));
                  } else if (value !== undefined && value !== null) {
                    usp.append(key, value);
                  }
                });
                return usp.toString();
              },
            });

            if (Array.isArray(payload)) return payload;
            if (Array.isArray(payload?.results)) return payload.results;
            if (payload?.id) return [payload];
            return [];
          } catch (secondErr) {
            console.warn(`Failed loading chambers for siteId=${siteId}`, {
              firstErr,
              secondErr,
            });
            return [];
          }
        }
      });

      const settledResults = await Promise.all(requests);
      return dedupeById(settledResults.flat());
    },
    [basePrefix, shouldUseAdminApi],
  );

  const hydrateTenantView = useCallback(
    async (targetTenantId, accessibleSites) => {
      const scopedAccessibleSites = accessibleSites || allAccessibleSites;
      const selectedTenantSites = filterSitesByTenant(scopedAccessibleSites, targetTenantId);
      const selectedSiteIds = selectedTenantSites
        .map((site) => get(site, 'id'))
        .filter((id) => id !== undefined && id !== null);

      setBootLoading(true);
      setSeeAllMode(false);
      setFilterCompany(targetTenantId);
      setFilterSite(undefined);
      setPage(1);

      const rawChambers = await fetchAllChambersForSites(selectedSiteIds);
      const chambers = normalizeChambers(rawChambers, selectedTenantSites);

      setAllowedSites(selectedTenantSites);
      setAllChambers(chambers);
      setBootLoading(false);
    },
    [allAccessibleSites, fetchAllChambersForSites],
  );

  const hydrateAllChambersView = useCallback(async () => {
    if (!hasGlobalChamberAccess) return;

    setBootLoading(true);
    setSeeAllMode(true);
    setSearchName('');
    setFilterCompany(undefined);
    setFilterSite(undefined);
    setPage(1);

    const allCacheKey = `${cacheBase}_ALL`;
    const cached = readJsonCache(allCacheKey);

    if (cached?.sites && cached?.chambers) {
      setAllowedSites(cached.sites);
      setAllChambers(cached.chambers);
      setBootLoading(false);
      return;
    }

    const siteIds = allAccessibleSites
      .map((site) => get(site, 'id'))
      .filter((id) => id !== undefined && id !== null);

    const rawChambers = await fetchAllChambersForSites(siteIds);
    const chambers = normalizeChambers(rawChambers, allAccessibleSites);

    setAllowedSites(allAccessibleSites);
    setAllChambers(chambers);

    writeJsonCache(allCacheKey, {
      sites: allAccessibleSites,
      chambers,
    });

    setBootLoading(false);
  }, [allAccessibleSites, cacheBase, fetchAllChambersForSites, hasGlobalChamberAccess]);

  useEffect(() => {
    if (!didClearRefreshCacheRef.current) {
      clearAllSessionCache();
      didClearRefreshCacheRef.current = true;
    }
  }, []);

  useEffect(() => {
    if ((isTenantRole || isSiteRole) && tenantIdentifier) {
      setFilterCompany(tenantIdentifier);
    }
  }, [isTenantRole, isSiteRole, tenantIdentifier]);

  const handleSelectChange = (type, value) => {
    setServiceSelections((prev) => ({ ...prev, [type]: value }));
  };

  const handleOnDetails = (record) => {
    setSelectedChamberForDetails(record);
    setDetailsModalOpen(true);
  };

  const handleOnQrCode = (qrcode) => {
    setQRModalStateLoading(true);
    setQRModalState(true);
    setTimeout(() => {
      const canvas = document.getElementById('qr-canvas');
      QR.toCanvas(canvas, qrcode, { width: 250, height: 250 });
      setQRModalStateLoading(false);
    }, 400);
  };

  const handleOnMoveSubmit = async () => {
    try {
      await dispatch(moveChamber()).unwrap();
      toast.success(eng.toast.moveChamberSuccessfully);
    } catch (err) {
      console.error('Move chamber failed:', err);
      toast.error(err?.message || eng.chamberMoveError);
    }
    setShowMove(false);
  };

  const handleOnCancelMove = () => {
    setShowMove(false);
    dispatch(claerMoveInfo());
  };

  const handleOnMove = (data) => {
    const {
      tenant: { identifier, name: tenantNameValue },
      id: chamberId,
      name: chamberName,
      site: { id: siteId, name: siteName },
    } = data;

    dispatch(setMoveFromCompany({ identifier, tenantName: tenantNameValue }));
    dispatch(setMoveFromSite({ siteId, siteName }));
    dispatch(setMoveSelectedChamber({ chamberId, chamberName }));
    dispatch(setMoveToCompany(''));
    dispatch(setMoveToSite(''));
    setShowMove(true);
  };

  const handleOnSchedule = (data) => {
    const {
      tenant: { identifier, name: tenantNameValue },
      id: chamberId,
      name: chamberName,
      site: { id: siteId, name: siteName },
    } = data;

    dispatch(setMoveFromCompany({ identifier, tenantName: tenantNameValue }));
    dispatch(setMoveFromSite({ siteId, siteName }));
    dispatch(setMoveSelectedChamber({ chamberId, chamberName }));

    setSelectedChamberForSchedule(data);
    setShowScheduleModal(true);
  };

  const handleToggleChamber = async (chamberId, currentValue) => {
    const newValue = !currentValue;
    //console.log('Toggle initiated:', { chamberId, currentValue, newValue });
    setTogglingChamberIds((prev) => new Set([...prev, chamberId]));

    try {
      console.log('Dispatching enableDisabledChamber...');
      const result = await dispatch(enableDisabledChamber({ value: newValue, id: chamberId })).unwrap();
     // console.log('Toggle successful, result:', result);
      
      // Update local state to reflect the toggle
      setAllChambers((prev) => {
        const updated = prev.map((chamber) =>
          chamber.id === chamberId ? { ...chamber, isEnabled: newValue } : chamber
        );
        console.log('Updated allChambers:', updated);
        return updated;
      });
      
      const message = `Chamber was successfully ${newValue ? 'enabled' : 'disabled'}`;
      console.log('Showing success message:', message);
      toast.success(message);
    } catch (err) {
      console.error('Toggle error:', err);
      toast.error(err?.message || err?.data?.message || 'Failed to update chamber status');
    } finally {
      setTogglingChamberIds((prev) => {
        const next = new Set(prev);
        next.delete(chamberId);
        return next;
      });
    }
  };

  useEffect(() => {
    // console.log('==== AUTH DEBUG ====');
    // console.log('user:', user);
    // console.log('resolvedRoles:', resolvedRoles);
    // console.log(
    //   'isSiteRole:',
    //   isSiteRole,
    //   'isTenantRole:',
    //   isTenantRole,
    //   'isMinearcRole:',
    //   isMinearcRole,
    //   'isSuperAdminRole:',
    //   isSuperAdminRole,
    // );
    // console.log('tenantIdentifier:', tenantIdentifier);
    // console.log('assignedSiteIds:', assignedSiteIds);
    // console.log('assignedSiteIdsSet:', Array.from(assignedSiteIdsSet));
    // console.log('hasGlobalChamberAccess:', hasGlobalChamberAccess);
    // console.log('canEditChambers:', canEditChambers);
    // console.log('shouldUseAdminApi:', shouldUseAdminApi);
    // console.log('isAdminApi:', isAdminApi);
    // console.log('====================');
  }, [
    user,
    resolvedRoles,
    isAdminApi,
    isSiteRole,
    isTenantRole,
    isMinearcRole,
    isSuperAdminRole,
    hasGlobalChamberAccess,
    canEditChambers,
    shouldUseAdminApi,
    tenantIdentifier,
    assignedSiteIds,
    assignedSiteIdsSet,
  ]);

  useEffect(() => {
    if (!hasGlobalChamberAccess) return;
    dispatch(fetchMinimalCompanyList());
  }, [dispatch, hasGlobalChamberAccess]);

  useEffect(() => {
    if (didBootRef.current) return;
    didBootRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        setBootLoading(true);

        const sitesCacheKey = `${cacheBase}_ACCESSIBLE_SITES`;
        let accessibleSites = readJsonCache(sitesCacheKey);

        if (!accessibleSites || !Array.isArray(accessibleSites)) {
          const fetchedSites = await fetchAllSites();
          accessibleSites = scopeSitesForUser(fetchedSites);
          writeJsonCache(sitesCacheKey, accessibleSites);
        }

        if (cancelled) return;

        setAllAccessibleSites(accessibleSites);

        if (!accessibleSites.length) {
          setAllowedSites([]);
          setAllChambers([]);
          setBootLoading(false);
          return;
        }

        if (isTenantRole || isSiteRole) {
          const forcedTenantId =
            tenantIdentifier || getTenantIdentifierFromSite(accessibleSites[0]) || undefined;

          if (forcedTenantId) {
            await hydrateTenantView(forcedTenantId, accessibleSites);
          } else {
            const siteIds = accessibleSites
              .map((site) => get(site, 'id'))
              .filter((id) => id !== undefined && id !== null);

            const rawChambers = await fetchAllChambersForSites(siteIds);
            const chambers = normalizeChambers(rawChambers, accessibleSites);

            if (cancelled) return;
            setFilterCompany(undefined);
            setAllowedSites(accessibleSites);
            setAllChambers(chambers);
            setBootLoading(false);
          }
          return;
        }

        const companiesPayload = await dispatch(fetchMinimalCompanyList()).unwrap();
        const normalizedCompanies = buildTenantOptionsFromCompanies(companiesPayload);
        const firstTenantId = normalizedCompanies[0]?.value;

        if (firstTenantId) {
          await hydrateTenantView(firstTenantId, accessibleSites);
        } else {
          const fallbackTenantOptions = buildTenantOptionsFallback(accessibleSites);
          const fallbackFirstTenantId = fallbackTenantOptions[0]?.value;

          if (fallbackFirstTenantId) {
            await hydrateTenantView(fallbackFirstTenantId, accessibleSites);
          } else {
            setAllowedSites(accessibleSites);
            setAllChambers([]);
            setBootLoading(false);
          }
        }
      } catch (err) {
        console.error('BOOT failed:', err);
        if (!cancelled) {
          setAllowedSites([]);
          setAllChambers([]);
          setBootLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    cacheBase,
    dispatch,
    fetchAllSites,
    fetchAllChambersForSites,
    hydrateTenantView,
    isSiteRole,
    isTenantRole,
    scopeSitesForUser,
    tenantIdentifier,
  ]);

  const siteOptions = useMemo(() => {
    const sourceSites = seeAllMode ? allAccessibleSites : allowedSites;
    const map = new Map();

    sourceSites.forEach((site) => {
      const id = get(site, 'id');
      const name = get(site, 'name');
      const siteTenantId = getTenantIdentifierFromSite(site) || tenantIdentifier;

      if (filterCompany && normalizeId(siteTenantId) !== normalizeId(filterCompany)) return;
      if (id !== undefined && id !== null) {
        map.set(id, name || id);
      }
    });

    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [seeAllMode, allAccessibleSites, allowedSites, filterCompany, tenantIdentifier]);

  useEffect(() => {
    if (!filterSite) return;
    const exists = siteOptions.some((o) => normalizeId(o.value) === normalizeId(filterSite));
    if (!exists) setFilterSite(undefined);
  }, [filterSite, siteOptions]);

  const filteredChambers = useMemo(() => {
    const q = searchName.trim().toLowerCase();

    return allChambers.filter((c) => {
      const chamberTenantId = getTenantIdentifierFromChamber(c);
      const chamberSiteId = getSiteIdFromChamber(c);

      if (isSiteRole && !assignedSiteIdsSet.has(normalizeId(chamberSiteId))) return false;

      if (isTenantRole && !hasGlobalChamberAccess) {
        if (chamberTenantId && normalizeId(chamberTenantId) !== normalizeId(tenantIdentifier)) return false;
      }

      if (filterCompany && chamberTenantId && normalizeId(chamberTenantId) !== normalizeId(filterCompany)) {
        return false;
      }

      if (filterSite && normalizeId(chamberSiteId) !== normalizeId(filterSite)) return false;

      if (q) {
        const name = String(get(c, 'name', '')).toLowerCase();
        if (!name.includes(q)) return false;
      }

      return true;
    });
  }, [
    allChambers,
    filterCompany,
    filterSite,
    searchName,
    isSiteRole,
    isTenantRole,
    hasGlobalChamberAccess,
    tenantIdentifier,
    assignedSiteIdsSet,
  ]);

  const pagedChambers = useMemo(() => {
    const start = (currentPage - 1) * DEFAULT_PAGE_SIZE;
    const end = start + DEFAULT_PAGE_SIZE;
    return filteredChambers.slice(start, end);
  }, [filteredChambers, currentPage]);

  const emptyViewDescription = useMemo(() => {
    if (bootLoading || minimalCompanyListLoading) return 'Loading chambers...';
    if (filteredChambers.length === 0) return searchName ? 'No chambers match your search.' : 'No chambers found.';
    return '';
  }, [bootLoading, filteredChambers.length, minimalCompanyListLoading, searchName]);

  const scheduleTableData = useMemo(
    () => [
      { key: '1', type: 'Commission' },
      { key: '2', type: 'Audit' },
      { key: '3', type: 'Service' },
    ],
    [],
  );

  const handleCompanyChange = async (val) => {
    setPage(1);
    setFilterSite(undefined);

    if (!seeAllMode && hasGlobalChamberAccess) {
      await hydrateTenantView(val);
      return;
    }

    setFilterCompany(val);
  };

  const handleSeeAllChambers = async () => {
    await hydrateAllChambersView();
  };

  const selectedSiteLabel = useMemo(() => {
    if (!filterSite) return '';
    const match = siteOptions.find((o) => normalizeId(o.value) === normalizeId(filterSite));
    return match?.label || '';
  }, [filterSite, siteOptions]);

  const handleDownloadServiceExpiryReport = async () => {
    if (!filterSite || expiryReportLoading) return;

    setExpiryReportLoading(true);
    try {
      await downloadServiceExpiryReport({ siteId: filterSite, siteName: selectedSiteLabel });
    } finally {
      setExpiryReportLoading(false);
    }
  };

  return (
    <SubPageLayout
      title={<>Chambers</>}
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {filterSite && (
            <Tooltip
              title={`Download the MineArc Service Expiry Report for ${selectedSiteLabel || 'the selected site'}`}
            >
              <Button
                icon={<FileExcelOutlined />}
                loading={expiryReportLoading}
                onClick={handleDownloadServiceExpiryReport}
              >
                Service Expiry Report
              </Button>
            </Tooltip>
          )}
          {featureAddChambers && (
            <Link to="/chambers/add">
              <Button type="primary" icon={<PlusOutlined />}>
                Add Chamber
              </Button>
            </Link>
          )}
        </div>
      }
      subtitle="Chambers"
    >
      <div
        className="chambers-search-panel"
        style={{
          marginBottom: 8,
          background: '#ffffff',
          border: '1px solid #e1e5e8',
          borderRadius: 8,
          padding: 8,
          boxShadow: '0 2px 8px rgba(17, 20, 45, 0.04)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 6,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 7,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 133, 64, 0.10)',
                color: '#008540',
                border: '1px solid rgba(0, 133, 64, 0.12)',
              }}
            >
              <FilterOutlined />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#252a2e' }}>Search & Filters</div>
              {/* <div style={{ fontSize: 12, color: '#8a8fa3', marginTop: 2 }}>
                {seeAllMode
                  ? 'Showing all chambers. Filters now work instantly.'
                  : 'Showing one tenant at a time for faster loading.'}
              </div> */}
            </div>
          </div>

          {CanViewAllChambers && (
            <Button
              icon={<ClearOutlined />}
              onClick={handleSeeAllChambers}
              disabled={bootLoading || seeAllMode}
              style={{ borderRadius: 10, height: 38 }}
            >
              See All Chambers
            </Button>
          )}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.25fr 1fr 1fr',
            gap: 10,
            alignItems: 'end',
          }}
        >
          <FilterField label="Search Chamber">
            <Input
              value={searchName}
              onChange={(e) => {
                setSearchName(e.target.value);
                setPage(1);
              }}
              allowClear
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Type a chamber name..."
              style={{
                borderRadius: 6,
                height: 36,
                borderColor: '#d9dee8',
                boxShadow: 'none',
              }}
            />
          </FilterField>

          <FilterField label="Company">
            <Select
              className="chambers-filter-select"
              allowClear={false}
              showSearch
              optionFilterProp="label"
              value={filterCompany}
              onChange={handleCompanyChange}
              placeholder="All companies"
              options={companyOptions}
              disabled={companyLocked}
              style={{ width: '100%' }}
              size="middle"
              loading={minimalCompanyListLoading && hasGlobalChamberAccess}
            />
          </FilterField>

          <FilterField label="Site">
            <Select
              className="chambers-filter-select"
              allowClear
              showSearch
              optionFilterProp="label"
              value={filterSite}
              onChange={(val) => {
                setFilterSite(val);
                setPage(1);
              }}
              placeholder={isSiteRole ? 'Assigned sites' : 'All sites'}
              options={siteOptions}
              disabled={siteLocked}
              style={{ width: '100%' }}
              size="middle"
            />
          </FilterField>
        </div>
      </div>

      <Spin spinning={bootLoading || (minimalCompanyListLoading && hasGlobalChamberAccess && companyOptions.length === 0)}>
        <MyTable
          className="chambers-table"
          shellClassName="chambers-table-shell"
          columns={buildColumns(
            dispatch,
            hasGlobalChamberAccess,
            canEditChambers,
            handleOnQrCode,
            handleOnMove,
            handleOnSchedule,
            handleOnDetails,
            togglingChamberIds,
            handleToggleChamber,
          )}
          dataSource={pagedChambers}
          pagination={{
            position: ['none', 'bottomCenter'],
            pageSize: 10,
            total: filteredChambers.length,
            current: currentPage,
            onChange: (page) => setPage(page),
          }}
          scroll={{ y: 450 }}
          rowKey="id"
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyViewDescription} />,
          }}
        />
      </Spin>

      <Modal
        title="Print QR"
        cancelButtonProps={{ style: { display: 'none' } }}
        visible={QRModalState}
        onCancel={() => setQRModalState(false)}
        onOk={() => {
          printJS({
            printable: document.querySelector('#qr-canvas').toDataURL(),
            type: 'image',
            imageStyle: 'width:100%',
          });
        }}
        okText="Print"
      >
        <Spin spinning={QRModalStateLoading}>
          <div className="d-flex jc-center">
            <canvas id="qr-canvas" />
          </div>
        </Spin>
      </Modal>

      <Modal
        title="Schedule Service"
        visible={showScheduleModal}
        width={800}
        onCancel={() => setShowScheduleModal(false)}
        onOk={() => {
          console.log('Selections:', serviceSelections);
          console.log('Technician:', selectedTechnician);
          console.log('Date:', selectedDate);
          setShowScheduleModal(false);
        }}
      >
        <div style={{ padding: '25px' }}>
          <br />
          <h3>
            <strong>Chamber:</strong> {selectedChamberForSchedule?.name}
          </h3>
          <h3>
            <strong>Site:</strong> {selectedChamberForSchedule?.site?.name}
          </h3>
          <br />

          <Table
            dataSource={scheduleTableData}
            pagination={false}
            bordered
            columns={[
              { title: 'Service Type', dataIndex: 'type', key: 'type' },
              {
                title: 'Action',
                key: 'action',
                render: (_, record) => (
                  <Select
                    value={serviceSelections[record.type] || 'No'}
                    onChange={(value) => handleSelectChange(record.type, value)}
                    style={{ width: 100 }}
                  >
                    <Select.Option value="Yes">Yes</Select.Option>
                    <Select.Option value="No">No</Select.Option>
                  </Select>
                ),
              },
            ]}
          />
          <br />

          <div style={{ marginTop: 24 }}>
            <p>
              <strong>Assign Technician:</strong>
            </p>
            <Select
              value={selectedTechnician}
              onChange={setSelectedTechnician}
              style={{ width: 240 }}
              placeholder="Select Technician"
            >
              <Select.Option value="tech1">ndumbi.mulumba@minearc.com</Select.Option>
              <Select.Option value="tech2">danie.breytenbach@minearc.com</Select.Option>
              <Select.Option value="tech3">mike.johnson@minearc.com</Select.Option>
            </Select>
          </div>

          <div style={{ marginTop: 16 }}>
            <p>
              <strong>Schedule Date:</strong>
            </p>
            <DatePicker
              value={selectedDate ? moment(selectedDate) : null}
              onChange={(date, dateString) => setSelectedDate(dateString)}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="Chamber Details"
        visible={detailsModalOpen}
        width={900}
        footer={null}
        onCancel={() => {
          setDetailsModalOpen(false);
          setSelectedChamberForDetails(null);
        }}
        destroyOnClose
        wrapClassName="chamber-details-modal-wrap"
        bodyStyle={{ background: '#fafafa' }}
      >
        <ChamberDetailsContent chamber={selectedChamberForDetails} />
      </Modal>

      <MoveChamberModal show={showMove} onCancel={handleOnCancelMove} onSubmit={handleOnMoveSubmit} />
    </SubPageLayout>
  );
};

export default Chambers;